import type { GetKeyMessageGroupsForSegment } from "@/data-access/news-feeds/queries/key-message-queries";
import type { ShareVoiceSegmentFragment } from "@/data-access/share-of-voice/fragments/segment-fragment";
import type { ResultOf } from "gql.tada";
import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import { createSegmentActions } from "./segment.actions";

export type Segment = ResultOf<typeof ShareVoiceSegmentFragment>;
export type KeyMessageGroup = ResultOf<
  typeof GetKeyMessageGroupsForSegment
>["keyMessageGroups"][0];

export type SoVModalStateSlice = {
  isCreateOrEditSegmentModalOpen: boolean;
  isDeleteSegmentModalOpen: boolean;
  setIsCreateOrEditSegmentModalOpen: (isOpen: boolean) => void;
  setIsDeleteSegmentModalOpen: (isOpen: boolean) => void;
};

export type CreateSegmentStateSlice = {
  feedId: null | string;
  color: string;
  keyMessageId: null | string;
  savedViewId: null | string;
  name: string;
  isCreatingSegment: boolean;
  createSegmentState: "initial" | "complete";
  setFeedId: (feedId: string) => void;
  setKeyMessageId: (keyMessageId: string | null) => void;
  setSavedViewId: (savedViewId: string | null) => void;
  setSegmentColor: (color: string) => void;
  setSegmentName: (segmentName: string) => void;
  resetSegment: () => void;
};

export type UpdateSegmentStateSlice = {
  updateSegmentId: null | string;
  isUpdatingSegment: boolean;
  updateSegmentState: "initial" | "complete";
  onEditSegment: (params: {
    segmentId: string;
    color: string;
    name: string;
  }) => void;
};

export type DeleteSegmentStateSlice = {
  deleteSegmentId: null | string;
  isDeletingSegment: boolean;
  setDeleteSegmentId: (id: string | null) => void;
  resetDeleteSegment: () => void;
};

export type SegmentDataFetchSlice = {
  isFetchingSegments: boolean;
  segments: Set<Segment>;
  isFetchingKeyMessagesAndGroups: boolean;
  keyMessagesAndGroups: KeyMessageGroup[];
  resetFetchedSegments: () => void;
};

export type SegmentStateSlice = SoVModalStateSlice &
  CreateSegmentStateSlice &
  UpdateSegmentStateSlice &
  DeleteSegmentStateSlice &
  SegmentDataFetchSlice;

export const createSegmentInitialState = {
  feedId: null,
  color: "#8E56D5",
  isCreateOrEditSegmentModalOpen: false,
  keyMessageId: null,
  savedViewId: null,
  name: "",
  isCreatingSegment: false,
  createSegmentState: "initial" as const,
};

export const updateSegmentInitialState = {
  updateSegmentId: null,
  updateFeedId: null,
  isUpdatingSegment: false,
  updateSegmentState: "initial" as const,
};

export const deleteSegmentInitialState = {
  deleteSegmentId: null,
  isDeletingSegment: false,
  isDeleteSegmentModalOpen: false,
};

export const segmentsInitialState = {
  isFetchingSegments: false,
  segments: new Set<Segment>(),
  isFetchingKeyMessagesAndGroups: false,
  keyMessagesAndGroups: [],
};

const createSegmentSlice: StateCreator<
  SegmentStateSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  //** Create Segment Slice */
  ...createSegmentInitialState,
  ...updateSegmentInitialState,
  ...deleteSegmentInitialState,
  ...segmentsInitialState,
  ...createSegmentActions(set, get, api),
});

const useSegmentStore = create<SegmentStateSlice>()(
  devtools(createSegmentSlice, {
    name: "sov-segment-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useSegmentStore;
